import React, { useMemo, useState } from 'react';
import { checkPasswordStrength, SPECIAL_CHARS_DISPLAY } from '../utils/passwordPolicy';
import PasswordInput from '../components/PasswordInput';

// Shared final step for every password-reset/change path in the app —
// ForgotPassword.jsx's phone-OTP path (inline, same tab), ResetPassword.jsx
// (the email link's landing page), and ChangePasswordRequired.jsx (the
// mandatory prompt after an admin-initiated reset). Each caller already
// proved identity by the time this renders (a resetToken, or simply being
// logged in) — this just collects and submits the new value via whatever
// onSubmit(password) the caller provides, since each path hits a
// differently-authenticated backend endpoint.

const lbl = {
  display: 'block',
  fontSize: '13px',
  fontWeight: '500',
  color: '#5F5E5A',
  marginBottom: '5px',
};

const inp = {
  width: '100%',
  padding: '10px 14px',
  border: '1.5px solid #D3D1C7',
  borderRadius: '8px',
  fontSize: '15px',
  boxSizing: 'border-box',
  outline: 'none',
};

const btn = {
  width: '100%',
  padding: '14px',
  background: 'linear-gradient(135deg, #009FE3 0%, #0072B1 100%)',
  color: '#fff',
  border: 'none',
  borderRadius: '10px',
  fontSize: '16px',
  fontWeight: '600',
  cursor: 'pointer',
  marginTop: '8px',
};

const btnDisabled = { ...btn, opacity: 0.6, cursor: 'not-allowed' };

// onSubmit(password) must return { ok: true } or { error: string }.
export default function SetNewPasswordStep({ onSubmit, onSuccess, description, submitLabel }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState({});
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);

  // Live, as-you-type feedback — previously only checked on submit, so a
  // weak password (or a mismatch) only surfaced after clicking through,
  // instead of catching it the moment it's typed.
  const passwordError = useMemo(() => checkPasswordStrength(password), [password]);
  const confirmError = useMemo(() => (
    confirm && password !== confirm ? "Passwords don't match." : null
  ), [password, confirm]);

  const showPasswordError = (touched.password || attemptedSubmit) && passwordError;
  const showConfirmError = (touched.confirm || attemptedSubmit) && confirmError;

  async function handleSubmit(e) {
    e.preventDefault();
    if (passwordError || confirmError) {
      setAttemptedSubmit(true);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await onSubmit(password);
      if (!result?.ok) {
        setError(result?.error || 'Could not reset your password.');
        return;
      }
      onSuccess();
    } catch (e) {
      setError('Could not reach the account service.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <p style={{ textAlign: 'center', color: '#666', fontSize: '14px', marginBottom: '20px' }}>
        {description || 'Choose a new password for your account.'}
      </p>

      {error && (
        <div style={{
          background: '#FDECEC', color: '#B3261E', padding: '10px 14px',
          borderRadius: '8px', fontSize: '13px', marginBottom: '16px',
        }}>
          {error}
        </div>
      )}

      <div style={{ marginBottom: '16px' }}>
        <label style={lbl}>New password *</label>
        <PasswordInput
          style={inp}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onBlur={() => setTouched((prev) => ({ ...prev, password: true }))}
          autoFocus
          required
        />
        {showPasswordError ? (
          <p style={{ color: '#B3261E', fontSize: '12px', margin: '4px 0 0' }}>{passwordError}</p>
        ) : (
          <p style={{ color: '#8a8a86', fontSize: '12px', margin: '4px 0 0' }}>
            Must be 8+ characters with letters, numbers, and a special character ({SPECIAL_CHARS_DISPLAY})
          </p>
        )}
      </div>
      <div style={{ marginBottom: '16px' }}>
        <label style={lbl}>Confirm new password *</label>
        <PasswordInput
          style={inp}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          onBlur={() => setTouched((prev) => ({ ...prev, confirm: true }))}
          required
        />
        {showConfirmError && (
          <p style={{ color: '#B3261E', fontSize: '12px', margin: '4px 0 0' }}>{confirmError}</p>
        )}
      </div>

      <button type="submit" disabled={loading || !password || !confirm} style={(loading || !password || !confirm) ? btnDisabled : btn}>
        {loading ? 'Saving…' : (submitLabel || 'Reset Password')}
      </button>
    </form>
  );
}
